import React, {useEffect, useState} from "react";
import {wdiCore, WdiModalAppResult, WdiModalProps} from "@/components/WdiControls";
import {ResponseStatus} from "@/utils/request";
import {Input, TablePaginationConfig, Tooltip} from "antd";
import {ColumnsType, SorterResult} from "antd/lib/table/interface";
import {FilterColumn, GridFilterForm} from "@/components/GridWidget/GridFilterForm";
import {WdiSelect} from "@/components/WdiControls/WdiSelect";
import {WdiModal} from "@/components/WdiControls/WdiModalForm";
import {WdiTable} from "@/components/WdiControls/WdiTable";
import {CustSys} from "@/services/mdm/custsys";
import {Shop} from "@/services/mdm/shop";

export type ShopItem = {
  shopId: number;
  codeOrName: string | null;
};

export type ShopSelectAppProps = {
  value?: ShopItem;
  onChange?: (value?: ShopItem) => void;
  disabled?: boolean;
  soldToCode: string;
  codeOrName?: string;
  onBeforeOpen?: () => boolean | void;  // 添加 onBeforeOpen 属性
};

type ShopSelectModalAppProps = {
  visible: boolean;
  title: string;
  id: any;
  data: any;
  onClose: () => void;
  onSave: (result?: WdiModalAppResult) => void;
  soldToCode: string;
};

const ShopSelectApp: React.FC<ShopSelectAppProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const [modalSoldToCode, setModalSoldToCode] = useState<string>(props.soldToCode);

  const onClose = () => {
    setModalProps({visible: false, title: "", id: null, data: null});
  };

  const onSave = (result?: WdiModalAppResult) => {
    setModalProps({visible: false, title: "", id: null, data: null});
    if (result && result.code === ResponseStatus.SUCCESS && props.onChange) {
      props.onChange(result.data);
    }
  };

  const handleClick = () => {
    // 如果提供了 onBeforeOpen 函数，则调用它
    if (props.onBeforeOpen) {
      const canOpen = props.onBeforeOpen();
      // 如果 onBeforeOpen 返回 false，则不打开弹窗
      if (canOpen === false) {
        return;
      }
    }

    setModalProps({
      visible: true,
      title: "查询门店",
      id: null,
      data: props.value
    });
    setModalSoldToCode(props.soldToCode);
  };

  const newProps: any = {
    onClick: handleClick,
  };

  return (
    <>
      <Tooltip title={props.value?.codeOrName}>
        <Input
          readOnly={props.disabled}
          {...newProps}
          placeholder="请选择门店"
          value={props.value?.codeOrName ? props.value?.codeOrName : props.codeOrName}
        />
      </Tooltip>
      {
        modalProps.visible && !props.disabled &&
        <ShopSelectModalApp
          visible={modalProps.visible}
          title={modalProps.title || ""}
          id={modalProps.id}
          data={modalProps.data}
          onClose={onClose}
          onSave={onSave}
          soldToCode={modalSoldToCode}
        />
      }
    </>
  )
};

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any,
};

const ShopSelectModalApp: React.FC<ShopSelectModalAppProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<API.Shop>();
  const [selectedRowKey, setSelectedRowKey] = useState<any>();

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
    },
    sorter: null,
    filterOptions: {
      soldToCode: props.soldToCode
    },
  });

  const setRowClass = (record: API.Shop) => {
    return record.shopId == selectedRowKey ? 'ant-table-row-selected' : ''
  };

  const filterColumns: FilterColumn[] = [
    {
      title: '门店编号',
      dataIndex: 'shopCode',
      render: () => {
        return <Input allowClear/>
      }
    },
    {
      title: '门店名称',
      dataIndex: 'shopName',
      render: () => {
        return <Input allowClear/>
      }
    },
    {
      title: '客户集团',
      dataIndex: 'custSysCode',
      span: 4,
      render: () => {
        return <WdiSelect {...{
          placeholder: "请选择客户集团",
          showSearch: true,
          remote: true,
          dataLoading: async (params) => {
            const response = await CustSys.ajaxSearch(Object.assign({
              withBoHqPermissions: true,
              isActive: true
            }, params));
            return response.data.map((item) => {
              return {value: item.custSysCode, label: item.codeOrName};
            });
          }
        }} />
      },
    }
  ];

  const columns: ColumnsType<any> = [
    {
      title: '门店编号',
      width: 150,
      dataIndex: 'shopCode',
      ellipsis: true,
      render: (_, record) => (
        <span>{record.shopCode}</span>
      ),
    },
    {
      title: '门店名称',
      width: 150,
      dataIndex: 'shopName',
      ellipsis: true,
      render: (_, record) => (
        <span>{record.shopName}</span>
      ),
    },
    {
      title: '客户集团编号',
      width: 150,
      dataIndex: 'custSysCode',
      ellipsis: true,
      render: (_, record) => (
        <span>{record.custSysCode}</span>
      ),
    },
    {
      title: '客户集团名称',
      width: 150,
      dataIndex: 'custSysName',
      ellipsis: true,
      render: (_, record) => (
        <span>{record.custSysName}</span>
      ),
    },
  ];

  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions: any = {
      soldToCode: props.soldToCode
    };

    if (params.filterOptions) {
      const {
        shopCode,
        shopName,
        custSysCode
      } = params.filterOptions;

      if (!wdiCore.String.IsNullOrEmpty(shopCode)) {
        filterOptions.shopCode = shopCode;
      }
      if (!wdiCore.String.IsNullOrEmpty(shopName)) {
        filterOptions.shopName = shopName;
      }
      if (!wdiCore.String.IsNullOrEmpty(custSysCode)) {
        filterOptions.custSysCode = custSysCode;
      }
    }

    const req: any = {
      filterOptions: JSON.stringify(filterOptions),
      pagenum: params.pagination.current ? (params.pagination.current - 1) : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
    };

    Shop.queryForPicker(req).then(responseResult => {
      const data = responseResult.data;
      if (data) {
        setDataSource(data.records);
        setTableParams({
          pagination: {
            ...params.pagination,
            total: data.totalCount,
          },
          sorter: params.sorter,
          filterOptions: params.filterOptions,
        });
        setLoading(false);
        setSelectedRowKey(null);
      }
    });
  };

  const doSearch = (filterOptions: any) => {
    const mergedFilterOptions = {
      ...filterOptions,
      soldToCode: props.soldToCode
    };
    fetchData(Object.assign({}, tableParams, {filterOptions: mergedFilterOptions}));
  };

  const doSave = () => {
    if (selectedRowKey) {
      const data: ShopItem = {
        shopId: selectedRowKey,
        codeOrName: String(selectedRow?.codeOrName),
      };

      if (props.onSave) props.onSave({code: ResponseStatus.SUCCESS, data: data});
    }
    return true;
  };

  useEffect(() => {
    if (props.visible) {
      setLoading(true);
      const filterOptions: any = {
        soldToCode: props.soldToCode
      };

      const params = tableParams;
      const req: any = {
        filterOptions: JSON.stringify(filterOptions),
        pagenum: params.pagination.current ? (params.pagination.current - 1) : 0,
        pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
      };

      Shop.queryForPicker(req).then(responseResult => {
        const data = responseResult.data;
        if (data) {
          setTableParams({
            pagination: {
              ...params.pagination,
              total: data.totalCount,
            },
            sorter: params.sorter,
            filterOptions: filterOptions,
          });
          setDataSource(data.records);
          setLoading(false);
          setSelectedRowKey(null);
        }
      });
    }
  }, [props.visible, props.soldToCode]);

  return (
    <>
      <WdiModal
        title={props.title}
        visible={props.visible}
        width={900}
        onCancel={() => {
          props.onClose ? props.onClose() : null
        }}
        onOk={doSave}
      >
        <GridFilterForm doSeach={doSearch} columns={filterColumns}/>
        <WdiTable
          columns={columns}
          showPageRows={10}
          rowKey="shopId"
          dataSource={dataSource}
          pagination={{
            ...tableParams.pagination,
            onChange: (page, pageSize) => {
              fetchData(Object.assign({}, tableParams, {
                pagination: {
                  ...tableParams.pagination,
                  current: page,
                  pageSize: pageSize,
                },
              }));
            },
          }}
          loading={loading}
          rowClassName={setRowClass}
          onRow={(record: API.Shop) => {
            return {
              onClick: () => {
                setSelectedRow(record);
                setSelectedRowKey(record.shopId);
              },
              onDoubleClick: () => {
                setSelectedRow(record);
                setSelectedRowKey(record.shopId);
                doSave();
              },
            }
          }}
        />
      </WdiModal>
    </>
  )
};

export default ShopSelectApp;
